import Heading2 from "components/Heading/Heading2";
import LoginModal from "components/LoginModal/LoginModal";
import ServiceCard from "components/ServiceCard/ServiceCard";
import { Service } from "data/types";
import { useAppDispatch, useAppSelector, useScrollToTop } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { HiOutlineSquare3Stack3D } from "react-icons/hi2";
import { useLocation, useNavigate } from "react-router-dom";
import { filterAllServices } from "redux/features/service/serviceSlice";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Pagination from "shared/Pagination/Pagination";
import { SkeletonLoader } from "utils/SkeletonLoader";
import ServiceFiltersPage from "./ServiceFiltersPage";
import Header2 from "components/Header/Header2";

export interface SectionGridFilterCardProps {
  className?: string;
  data?: Service[];
  title?: string;
  selectedEvent?: string;
  region?: string;
}

const SectionGridFieldFilterCard: FC<SectionGridFilterCardProps> = ({ className = "" }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const dispatch = useAppDispatch();
  const serviceState = useAppSelector((state) => state?.service?.allServices);
  const totalPages = useAppSelector((state) => state?.service?.totalPages);
  const location = useLocation();
  useScrollToTop();
  const searchParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const handlePageChange = (pageNumber: number) => {
    const params = new URLSearchParams(location.search);
    params.set("page", String(pageNumber));

    navigate(`${location.pathname}?${params.toString()}`);
    setCurrentPage(pageNumber);
  };

  const pathname = location.pathname;
  const decodedPathname = decodeURIComponent(pathname);
  const afterField = decodedPathname.split("/field/")[1];

  const fields = searchParams.get("field")?.toLowerCase();
  const region = searchParams.get("region")?.toLowerCase();

  useEffect(() => {
    const page = Number(searchParams.get("page")) || 1;
    const fieldParam = fields || afterField || "";
    const regionParam = region || "";

    const filter = {
      fields: fieldParam || undefined,
      region: regionParam || undefined,
      page,
    };

    setCurrentPage(page);
    dispatch(filterAllServices(filter)).then(() => setIsLoading(false));
  }, [location.search]);

  const handleApplyFilters = (filters: any) => {
    const params = new URLSearchParams();
    if (filters.field) params.set("field", filters.field.toLowerCase());
    if (filters.region) params.set("region", filters.region.toLowerCase());
    params.set("page", "1");

    navigate(`${location.pathname}?${params.toString()}`);
  };

  const [showLoginModal, setShowLoginModal] = useState(false);
  return (
    <>
      <Helmet>
        <title>Event To Bee - Les Prestations</title>
        <meta name="description" content="Découvrez les détails des prestations sur Event To Bee." />
      </Helmet>

      <div className={`nc-SectionGridFilterCard ${className}`} data-nc-id="SectionGridFilterCard">
        {showLoginModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black opacity-50" />
            <div className="z-50 p-6 bg-white rounded-lg dark:bg-neutral-900">
              <div className="flex justify-end mb-4">
                <ButtonSecondary onClick={() => setShowLoginModal(false)}>X</ButtonSecondary>
              </div>
              <LoginModal LoginModal={(e) => setShowLoginModal(e)} />
            </div>
          </div>
        )}

        <Heading2
          heading={
            <div className="flex items-center">
              <HiOutlineSquare3Stack3D className="w-5 h-5 mr-2 text-gray-500 lg:w-5 lg:h-5" />
              <span>Prestations</span>
            </div>
          }
        />

        <ServiceFiltersPage
          fields={fields}
          region={region}
          onApplyFilters={handleApplyFilters}
          handleDelete={() => {}}
        />

        <br />
        <br />

        <div className="mb-16">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <SkeletonLoader />
              </div>
            </div>
          ) : serviceState?.length ? (
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
              {serviceState.map((service: Service, index: number) => (
                <div
                  key={service.id}
                  className="transition-all duration-300 transform hover:scale-105 hover:shadow-xl animate-fade-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <ServiceCard
                    data={service}
                    galleryImgs={service.images}
                    showLoginModal={showLoginModal}
                    setShowLoginModal={setShowLoginModal}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="flex items-center justify-center w-24 h-24 mb-6 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 dark:from-neutral-700 dark:to-neutral-600">
                <HiOutlineSquare3Stack3D className="w-12 h-12 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-gray-700 dark:text-gray-300">
                Aucune prestation disponible
              </h3>
              <p className="max-w-md text-gray-500 dark:text-gray-400">
                Aucune prestation ne correspond à vos critères de recherche actuels
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center mt-16 mb-10">
          {totalPages > 1 && (
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          )}
        </div>

        <div className="relative py-16">
          <Header2 title={"Devenez Prestataire et Proposez Votre Prestations !"} url={"/add-service"} />
        </div>
      </div>
    </>
  );
};

export default SectionGridFieldFilterCard;
